import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Pause, Play, Filter, Terminal } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api';

const LEVELS = ['ALL', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

const LEVEL_STYLE = {
  DEBUG:    { color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' },
  INFO:     { color: '#38bdf8', bg: 'rgba(56,189,248,0.08)'  },
  WARNING:  { color: '#fbbf24', bg: 'rgba(251,191,36,0.10)'  },
  ERROR:    { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  CRITICAL: { color: '#f43f5e', bg: 'rgba(244,63,94,0.15)'  },
};

const LEVEL_BADGE = {
  DEBUG:    'bg-slate-700 text-slate-300',
  INFO:     'bg-sky-900/60 text-sky-300',
  WARNING:  'bg-amber-900/60 text-amber-300',
  ERROR:    'bg-red-900/60 text-red-300',
  CRITICAL: 'bg-rose-900/80 text-rose-200 font-bold',
};

export default function AdminLogs() {
  const { user } = useAuth();
  const [lines, setLines]           = useState([]);
  const [level, setLevel]           = useState('ALL');
  const [loading, setLoading]       = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [search, setSearch]         = useState('');
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getAdminLogs(300, level);
      setLines(res.data.lines || []);
      setLastRefresh(new Date().toLocaleTimeString('fr-FR'));
    } catch (e) {
      // silencieux si le composant est démonté
    } finally {
      setLoading(false);
    }
  }, [level]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(load, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, load]);

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle size={36} style={{ color: '#DC2626' }} />
        <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
          Accès réservé au super-administrateur.
        </p>
      </div>
    );
  }

  const handleClear = async () => {
    await api.clearAdminLogs();
    setLines([]);
  };

  const displayed = search
    ? lines.filter(l =>
        l.msg.toLowerCase().includes(search.toLowerCase()) ||
        l.logger.toLowerCase().includes(search.toLowerCase())
      )
    : lines;

  const errorCount   = lines.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL').length;
  const warningCount = lines.filter(l => l.level === 'WARNING').length;

  return (
    <div data-testid="admin-logs-page" style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Terminal size={20} style={{ color: 'var(--primary)' }} />
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              Logs serveur
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Buffer en mémoire — 300 dernières lignes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {errorCount > 0 && (
            <span data-testid="logs-error-count"
              className="px-2 py-0.5 rounded text-xs font-bold"
              style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171' }}>
              {errorCount} erreur{errorCount > 1 ? 's' : ''}
            </span>
          )}
          {warningCount > 0 && (
            <span data-testid="logs-warning-count"
              className="px-2 py-0.5 rounded text-xs font-bold"
              style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
              {warningCount} warning{warningCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Filtre niveau */}
        <div className="flex items-center gap-1 rounded-lg p-1"
          style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
          <Filter size={12} style={{ color: 'var(--text-muted)', marginLeft: 4 }} />
          {LEVELS.map(lv => (
            <button
              key={lv}
              data-testid={`logs-filter-${lv.toLowerCase()}`}
              onClick={() => setLevel(lv)}
              className="px-2 py-0.5 rounded text-xs font-medium transition-all"
              style={level === lv
                ? { background: LEVEL_STYLE[lv]?.bg || 'var(--primary)', color: LEVEL_STYLE[lv]?.color || '#fff', fontWeight: 700 }
                : { color: 'var(--text-muted)' }
              }
            >
              {lv}
            </button>
          ))}
        </div>

        {/* Recherche */}
        <input
          data-testid="logs-search"
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-1.5 rounded-lg text-xs"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            outline: 'none',
            width: 180,
          }}
        />

        <div className="flex-1" />

        {/* Auto-refresh */}
        <button
          data-testid="logs-toggle-autorefresh"
          onClick={() => setAutoRefresh(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            background: autoRefresh ? 'rgba(56,189,248,0.1)' : 'var(--bg-secondary)',
            border: `1px solid ${autoRefresh ? '#38bdf8' : 'var(--border)'}`,
            color: autoRefresh ? '#38bdf8' : 'var(--text-muted)',
          }}
          title={autoRefresh ? 'Rafraîchissement auto actif (5s)' : 'Rafraîchissement auto désactivé'}
        >
          {autoRefresh ? <Pause size={12} /> : <Play size={12} />}
          {autoRefresh ? 'Auto (5s)' : 'Pausé'}
        </button>

        {/* Rafraîchir */}
        <button
          data-testid="logs-refresh-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
          }}
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Rafraîchir
        </button>

        {/* Vider */}
        <button
          data-testid="logs-clear-btn"
          onClick={handleClear}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          style={{
            background: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.3)',
            color: '#f87171',
          }}
        >
          <Trash2 size={12} />
          Vider
        </button>
      </div>

      {/* Dernière MAJ */}
      {lastRefresh && (
        <p className="text-xs mb-2" style={{ color: 'var(--text-disabled)' }}>
          Dernière MAJ : {lastRefresh} — {displayed.length} ligne{displayed.length > 1 ? 's' : ''} affichée{displayed.length > 1 ? 's' : ''}
        </p>
      )}

      {/* Table de logs */}
      <div
        data-testid="logs-table"
        className="rounded-xl overflow-hidden"
        style={{ border: '1px solid var(--border)', background: '#0d1117' }}
      >
        {displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <Terminal size={28} style={{ color: '#334155' }} />
            <p className="text-xs" style={{ color: '#334155' }}>
              {loading ? 'Chargement...' : 'Aucun log pour l\'instant.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e293b', background: '#0d1117' }}>
                  <th className="text-left px-3 py-2" style={{ color: '#475569', fontWeight: 600, whiteSpace: 'nowrap', width: 80 }}>Heure</th>
                  <th className="text-left px-3 py-2" style={{ color: '#475569', fontWeight: 600, width: 90 }}>Niveau</th>
                  <th className="text-left px-3 py-2" style={{ color: '#475569', fontWeight: 600, width: 160 }}>Module</th>
                  <th className="text-left px-3 py-2" style={{ color: '#475569', fontWeight: 600 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((l, i) => {
                  const style = LEVEL_STYLE[l.level] || {};
                  const isError = l.level === 'ERROR' || l.level === 'CRITICAL';
                  return (
                    <tr
                      key={i}
                      data-testid={`log-row-${l.level.toLowerCase()}`}
                      style={{
                        background: isError ? 'rgba(248,113,113,0.04)' : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'),
                        borderBottom: '1px solid #0f172a',
                      }}
                    >
                      <td className="px-3 py-1.5 tabular-nums" style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                        {l.ts}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${LEVEL_BADGE[l.level] || ''}`}
                          style={{ fontSize: 10 }}>
                          {l.level}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" style={{ color: '#64748b', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {l.logger}
      </td>
                      <td className="px-3 py-1.5" style={{ color: style.color || '#94a3b8', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                        {l.msg}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
